import React from 'react';
import { Box, Carousel } from '@drone-js/core';
import { Loader } from 'component/common/Loader/Loader';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { selectPageTypeId } from 'store/slice/navigation';
import { selectActivityImagesFromDetailState } from 'component/page/detail/store/detail.selectors';
import ActivityCarouselImage from 'component/page/deprecatedDetail/DetailCard/ActivityCarousel/ActivityCarouselImage';
import 'component/page/deprecatedDetail/DetailCard/ActivityCarousel/ActivityCarousel.scss';
import { InView } from 'react-intersection-observer';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesImageCarouselSeenEventManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivitiesImageCarouselSeenEventFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';

interface Props {
    isMosaicEnabled: boolean;
    onImageClick: (e: React.MouseEvent, index: number) => void;
}

const ActivityCarousel: React.FC<Props> = React.memo((props): React.JSX.Element => {
    const [isLoadFinished, setIsLoadFinished] = React.useState(false);
    const images = useSelector(selectActivityImagesFromDetailState);
    const pageTypeId = useSelector(selectPageTypeId);
    const cms = useSelector(selectCmsFromBootstrap);

    const onSeen = (index: number) => (inView: boolean) => {
        if (!inView) return;
        trackElementEvent<ActivitiesImageCarouselSeenEventFieldsV2>(
            ActivitiesImageCarouselSeenEventManifestV2.ids,
            {},
            { action_element_index: index + 1, action_element_value: props.isMosaicEnabled.toString() },
        );
    };

    const createOnLoadOnFirstImage = (index: number) => {
        if (index === 0) {
            return () => {
                setIsLoadFinished(true);
                measureFinishRender({ component: 'CarouselComponent' }, pageTypeId);
            };
        }
        return undefined;
    };

    if (!images || images.length === 0) {
        return <Loader element="detail-image-carousel" />;
    }

    return (
        <Box mx="0" my={props.isMosaicEnabled ? '8' : '0'}>
            <div
                data-element-name="activities-image-carousel"
                data-testid="activities-image-carousel"
                className={`ActivityCarousel set-min-width ${isLoadFinished ? 'ActivityCarousel--loaded' : ''}`}
            >
                <Carousel
                    accessibilityLabel={cms[276248]}
                    accessibilityPageNumberLabel={cms[152705]}
                    accessibilityPreviousNavLabel={cms[130765]}
                    accessibilityNextNavLabel={cms[130706]}
                    pageControl="bottom"
                    cardWidth={props.isMosaicEnabled ? 294 : undefined}
                    pageButtons={props.isMosaicEnabled ? false : undefined}
                    display={props.isMosaicEnabled && images.length > 1 ? 'list' : 'single'}
                    fade={props.isMosaicEnabled && images.length > 1 ? 'visible' : undefined}
                >
                    {images.map((image, index) => (
                        <InView key={image.url} onChange={onSeen(index)} triggerOnce={true} threshold={1}>
                            <Box
                                as="button"
                                interactive="base.inverted"
                                p="0"
                                borderRadius="8"
                                mt="4"
                                ms={index === 0 ? '8' : '0'}
                                me={index === images.length - 1 ? '8' : '0'}
                                data-testid="activities-mosaic-gallery"
                                data-element-name="activities-mosaic-gallery"
                                data-element-index={images.length === 1 ? 1 : index + 1}
                                onClick={images.length === 1 ? undefined : (event) => props.onImageClick(event, index)}
                            >
                                <ActivityCarouselImage
                                    image={image}
                                    imageIndex={index}
                                    totalImages={images.length}
                                    onLoad={createOnLoadOnFirstImage(index)}
                                    isMosaicEnabled={props.isMosaicEnabled}
                                />
                            </Box>
                        </InView>
                    ))}
                </Carousel>
            </div>
        </Box>
    );
});
ActivityCarousel.displayName = 'ActivityCarousel';

export default ActivityCarousel;
